import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { api } from "@/lib/api";

import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Feed from "@/pages/Feed";
import Discover from "@/pages/Discover";
import Profile from "@/pages/Profile";
import Messages from "@/pages/Messages";
import VideosPage from "@/pages/VideosPage";
import Subscribe from "@/pages/Subscribe";
import PaymentResult from "@/pages/PaymentResult";
import Party from "@/pages/Party";
import Earnings from "@/pages/Earnings";
import Owner from "@/pages/Owner";
import TipPage from "@/pages/TipPage";
import AppShell from "@/components/AppShell";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        setUser(data.user);
        window.history.replaceState(null, "", "/feed");
        navigate("/feed", { replace: true });
      } catch {
        navigate("/auth", { replace: true });
      }
    })();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0E]">
      <div className="text-emerald-400 font-mono-nu animate-pulse">Signing you in…</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0E]">
        <div className="text-emerald-400 font-mono-nu animate-pulse">Loading NEXT UP…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/tip/:artistId" element={<TipPage />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/feed" element={<Feed />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:otherId" element={<Messages />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/earnings" element={<Earnings />} />
        <Route path="/owner" element={<Owner />} />
        <Route path="/party/:partyId" element={<Party />} />
        <Route path="/artist/:artistId" element={<Profile />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PlayerProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-center" richColors />
            <AppRouter />
          </BrowserRouter>
        </PlayerProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
